// Modules
import { useCallback } from "react";
import Head from "next/head";

// Components
import FilterAccordion from "components/FilterAccordion/FilterAccordion";

// Constants
import {FORMAZIONE_PAGE} from "constants/pages";
import {QueryStringPrimaryKeys} from "constants/urlEnum";

// Hooks
import { useFilters } from "hooks/useFilters";

// Redux
import { useAppSelector } from "redux/hooks";
import { categoriesSelector } from "redux/categories/categoriesSlice";
import { seoInfoPageSelector } from "redux/seoInformations/seoInformationsSlice";
import {activeFiltersSelector, urlPathSelector} from "redux/filters/filtersSlice";

// Types
import { Category, CurrentSelectedCategory } from "interfaces/category";

// Utils
import {isVisibleTag} from "utils/categories";
import {handleNewFilterParameterInPathname} from "utils/queryString";

const Filters = (): JSX.Element => {
	const clearAllFilters = useFilters();
	const seoInfoPage = useAppSelector(seoInfoPageSelector);
	const activeFilters = useAppSelector(activeFiltersSelector);
	const categories = useAppSelector(categoriesSelector);
	const urlPath = useAppSelector(urlPathSelector);

	const getCanonicalUrl = (): string => {
		let relativePathFromParameter: string | undefined;

		if (activeFilters.length) {
			relativePathFromParameter = handleNewFilterParameterInPathname(activeFilters[activeFilters.length - 1], urlPath, categories);
		}

		const baseUrl = `${process.env.NEXT_PUBLIC_DOMAIN}${FORMAZIONE_PAGE}`;
		const relativePath = relativePathFromParameter ? `/${relativePathFromParameter}` : "";
		const fullPath = `${baseUrl}${relativePath}`;

		const urlFromParameter = new URL(fullPath);

		const requestedUrl  = new URL(`${baseUrl}${urlPath ? `/${urlPath}` : ""}`);
		const searchParams = new URLSearchParams(requestedUrl.search);
		const pageQueryParam = searchParams.get(QueryStringPrimaryKeys.PAGE);

		return pageQueryParam !== null ? `${urlFromParameter.href}?${QueryStringPrimaryKeys.PAGE}=${pageQueryParam}` : urlFromParameter.href;
	};

	// The following function must be left here in order to have the correct opened macrocategories at the page loading
	const FilterAccordionArray = useCallback(() => {
		const buildActiveFilterCategory = (macroCategory: Category): CurrentSelectedCategory => {
			const activeCategoryByParameter = activeFilters.find((activeFilter) => activeFilter?.macroCategory?.urlParameter === macroCategory?.urlParameter) || {
				macroCategory,
				selectedTags: []
			};
			const updateSelectedTags = activeCategoryByParameter.selectedTags.filter((tag) => tag.urlParameter === macroCategory.urlParameter);
			return { ...activeCategoryByParameter, selectedTags: updateSelectedTags };
		};

		return categories.filter(tag => isVisibleTag(tag))
			.map((category) => {
				const activeFilterCategory = buildActiveFilterCategory(category);
				return <FilterAccordion key={category.urlParameter} activeFiltersByMacro={activeFilterCategory} />;
			});
	}, [activeFilters, categories]);

	return (
		<>
			<Head>
				<title>{`${seoInfoPage.title ? `${seoInfoPage.title} | ` : ""}Altalex`}</title>
				<meta
					name="description"
					content={
						seoInfoPage.metaDescription ||
						(seoInfoPage.title?.length
							? `${seoInfoPage.title} Altalex Formazione per i professionisti del diritto: iscriviti ai corsi e master di formazione giuridica legale online e in aula.`
							: "Altalex offre corsi di formazione giuridica legale, master brevi e corsi di preparazione in aula e online.")
					}
				/>
				<link rel="canonical" href={getCanonicalUrl()}/>
			</Head>
			{FilterAccordionArray()}
			{activeFilters.length != 0 && (
				<div className="clearFiltersBox">
					<div className="clearFiltersButton" onClick={clearAllFilters}>
						<span>Rimuovi tutti i filtri</span>
						<i className="fa fa-undo" />
					</div>
				</div>
			)}
		</>
	);
};

export default Filters;
